// widgets/plans/NewPlanDialog.jsx
import React, { useState } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    Button,
    Box,
    Alert,
    IconButton,
    FormControlLabel,
    Switch,
    InputAdornment,
    Stack,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import RequestService from '../../services/RequestService';
import { toCents } from '../../utils/toCents';

const AdminNewPlanDialog = ({ open, onClose, onSuccess }) => {
    const [form, setForm] = useState({
        name: '',
        priceReais: '',
        aiAutoReviewEnabled: false,
        aiReviewsPerMonth: 0,
        maxUsers: 1,
        aiTokenSoftCap: 0,
        active: true,
    });
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState(null);

    const handleChange = (e) => {
        const { name, value, type } = e.target;
        setForm((prev) => ({
            ...prev,
            [name]:
                type === 'number' ? (value === '' ? '' : Number(value)) : value,
        }));
    };

    const handleSwitch = (e) => {
        const { name, checked } = e.target;
        setForm((prev) => ({ ...prev, [name]: checked }));
    };

    const validate = () => {
        if (!form.name?.trim()) return 'Nome é obrigatório.';
        if (toCents(form.priceReais) < 0) return 'Preço não pode ser negativo.';
        if (form.aiReviewsPerMonth < 0) return 'AI reviews/mês deve ser >= 0.';
        if (form.maxUsers < 1) return 'Max users deve ser >= 1.';
        if (form.aiTokenSoftCap < 0) return 'AI token soft cap deve ser >= 0.';
        return null;
    };

    const handleSubmit = async () => {
        setError(null);
        const v = validate();
        if (v) {
            setError(v);
            return;
        }

        const payload = {
            name: form.name.trim(),
            priceCents: toCents(form.priceReais),
            aiAutoReviewEnabled: Boolean(form.aiAutoReviewEnabled),
            aiReviewsPerMonth: Number(form.aiReviewsPerMonth || 0),
            maxUsers: Number(form.maxUsers || 1),
            aiTokenSoftCap: Number(form.aiTokenSoftCap || 0),
            active: Boolean(form.active),
        };

        try {
            setSubmitting(true);
            await RequestService.createPlan(payload);
            onSuccess && onSuccess('Plano criado com sucesso!');
            onClose();
        } catch (err) {
            console.error(err);
            setError(
                'Erro ao criar plano. Verifique os dados e tente novamente.'
            );
        } finally {
            setSubmitting(false);
        }
    };

    const handleClose = () => {
        setError(null);
        onClose();
    };

    return (
        <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
            <DialogTitle>
                Novo Plano
                <IconButton
                    aria-label="close"
                    onClick={handleClose}
                    sx={{ position: 'absolute', right: 8, top: 8 }}
                >
                    <CloseIcon />
                </IconButton>
            </DialogTitle>

            <DialogContent>
                <Box
                    sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        gap: 2,
                        mt: 1,
                    }}
                >
                    <TextField
                        label="Nome"
                        name="name"
                        value={form.name}
                        onChange={handleChange}
                        fullWidth
                    />

                    <TextField
                        label="Preço (R$)"
                        name="priceReais"
                        value={form.priceReais}
                        onChange={handleChange}
                        fullWidth
                        InputProps={{
                            inputMode: 'decimal',
                        }}
                        placeholder="0,00"
                    />

                    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                        <TextField
                            label="AI reviews por mês"
                            name="aiReviewsPerMonth"
                            type="number"
                            value={form.aiReviewsPerMonth}
                            onChange={handleChange}
                            fullWidth
                            inputProps={{ min: 0 }}
                        />
                        <TextField
                            label="Max usuários"
                            name="maxUsers"
                            type="number"
                            value={form.maxUsers}
                            onChange={handleChange}
                            fullWidth
                            inputProps={{ min: 1 }}
                        />
                    </Stack>

                    <TextField
                        label="AI token soft cap"
                        name="aiTokenSoftCap"
                        type="number"
                        value={form.aiTokenSoftCap}
                        onChange={handleChange}
                        fullWidth
                        inputProps={{ min: 0 }}
                    />

                    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                        <FormControlLabel
                            control={
                                <Switch
                                    checked={form.aiAutoReviewEnabled}
                                    onChange={handleSwitch}
                                    name="aiAutoReviewEnabled"
                                />
                            }
                            label="Revisão automática por IA"
                        />
                        <FormControlLabel
                            control={
                                <Switch
                                    checked={form.active}
                                    onChange={handleSwitch}
                                    name="active"
                                />
                            }
                            label="Ativo"
                        />
                    </Stack>

                    {error && <Alert severity="error">{error}</Alert>}
                </Box>
            </DialogContent>

            <DialogActions>
                <Button onClick={handleClose} variant="outlined">
                    Cancelar
                </Button>
                <Button
                    onClick={handleSubmit}
                    variant="contained"
                    disabled={submitting}
                >
                    {submitting ? 'Criando...' : 'Criar Plano'}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default AdminNewPlanDialog;
